import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';
import { EmptyState, Tooltip } from '../Common/ReusableUI';
import { CardSkeleton, ListSkeleton } from '../Common/SkeletonLoaders';
import {
  Heart, Users, Calendar, Clock, Music, Palette, BookOpen, Sun, Star, Award,
  Target, TrendingUp, Search, Filter, Plus, Edit, CheckCircle, UserPlus,
  ClipboardList, BarChart3, Smile, Coffee, Flower2, Brain, ChevronDown,
  ChevronRight, MapPin, Phone, Mail, Shield, AlertCircle, X, Save, Eye,
  MessageSquare, FileText, Trash2, Zap, Bell, Printer, Download
} from 'lucide-react';

const ACTIVITY_TYPES = {
  Exercise: { color: 'bg-green-500', light: 'bg-green-100 text-green-800', icon: Target },
  Music: { color: 'bg-blue-500', light: 'bg-blue-100 text-blue-800', icon: Music },
  Art: { color: 'bg-pink-500', light: 'bg-pink-100 text-pink-800', icon: Palette },
  Social: { color: 'bg-yellow-500', light: 'bg-yellow-100 text-yellow-800', icon: Coffee },
  Spiritual: { color: 'bg-purple-500', light: 'bg-purple-100 text-purple-800', icon: Sun },
  Cognitive: { color: 'bg-indigo-500', light: 'bg-indigo-100 text-indigo-800', icon: Brain },
  Outing: { color: 'bg-teal-500', light: 'bg-teal-100 text-teal-800', icon: MapPin },
  Gardening: { color: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-800', icon: Flower2 },
};

const ENGAGEMENT_LEVELS = ['Highly Engaged', 'Engaged', 'Passive', 'Declined', 'Absent'];
const ENGAGEMENT_COLORS = {
  'Highly Engaged': 'bg-green-100 text-green-800',
  'Engaged': 'bg-blue-100 text-blue-800',
  'Passive': 'bg-yellow-100 text-yellow-800',
  'Declined': 'bg-orange-100 text-orange-800',
  'Absent': 'bg-gray-100 text-gray-600',
};
const ENGAGEMENT_TIPS = {
  'Highly Engaged': 'Actively participating, initiating interactions, visibly enjoying the activity',
  'Engaged': 'Participating willingly, following along, positive body language',
  'Passive': 'Present but minimal participation, observing rather than doing',
  'Declined': 'Chose not to participate when offered',
  'Absent': 'Did not attend the session',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];

const mockResidents = [
  { id: 'R001', name: 'Margaret Thompson', room: '12A', interests: ['Music', 'Gardening', 'Art'], lifeStory: 'Former music teacher, loves classical piano. Grew up on a farm in rural Victoria.', goals: [{ activity: 'Social', target: 3, current: 2, period: 'week' }, { activity: 'Music', target: 2, current: 2, period: 'week' }] },
  { id: 'R002', name: 'Harold Stevens', room: '15B', interests: ['Exercise', 'Social', 'Cognitive'], lifeStory: 'Retired engineer, avid chess player. Enjoys morning walks and crosswords.', goals: [{ activity: 'Exercise', target: 4, current: 3, period: 'week' }, { activity: 'Cognitive', target: 2, current: 1, period: 'week' }] },
  { id: 'R003', name: 'Dorothy Chen', room: '8C', interests: ['Art', 'Spiritual', 'Gardening'], lifeStory: 'Watercolour artist and Buddhist practitioner. Designed gardens for 30 years.', goals: [{ activity: 'Art', target: 3, current: 3, period: 'week' }, { activity: 'Gardening', target: 1, current: 1, period: 'week' }] },
  { id: 'R004', name: 'Frank Morrison', room: '22A', interests: ['Social', 'Music', 'Outing'], lifeStory: 'Former pub owner. Loves jazz, storytelling, and trips to the beach.', goals: [{ activity: 'Social', target: 4, current: 2, period: 'week' }, { activity: 'Outing', target: 1, current: 0, period: 'month' }] },
  { id: 'R005', name: 'Betty Williams', room: '5D', interests: ['Exercise', 'Gardening', 'Social'], lifeStory: 'Former netball player and garden show champion. Very social personality.', goals: [{ activity: 'Exercise', target: 3, current: 3, period: 'week' }, { activity: 'Social', target: 3, current: 2, period: 'week' }] },
  { id: 'R006', name: 'Arthur Nguyen', room: '18B', interests: ['Cognitive', 'Music', 'Spiritual'], lifeStory: 'Retired maths professor. Plays violin, practices tai chi daily.', goals: [{ activity: 'Cognitive', target: 3, current: 2, period: 'week' }, { activity: 'Music', target: 2, current: 1, period: 'week' }] },
  { id: 'R007', name: 'Edna Kowalski', room: '3A', interests: ['Art', 'Social', 'Coffee'], lifeStory: 'Former fashion designer from Poland. Loves hosting morning teas.', goals: [{ activity: 'Art', target: 2, current: 2, period: 'week' }, { activity: 'Social', target: 4, current: 3, period: 'week' }] },
  { id: 'R008', name: 'George Patel', room: '20C', interests: ['Exercise', 'Outing', 'Cognitive'], lifeStory: 'Former cricket coach. Enjoys outdoor activities and strategy games.', goals: [{ activity: 'Exercise', target: 4, current: 4, period: 'week' }, { activity: 'Outing', target: 2, current: 1, period: 'month' }] },
  { id: 'R009', name: 'Rose McCarthy', room: '11B', interests: ['Spiritual', 'Music', 'Gardening'], lifeStory: 'Church choir leader for 40 years. Grows herbs and makes herbal teas.', goals: [{ activity: 'Spiritual', target: 2, current: 2, period: 'week' }, { activity: 'Music', target: 2, current: 1, period: 'week' }] },
  { id: 'R010', name: 'James O\'Brien', room: '7D', interests: ['Social', 'Exercise', 'Music'], lifeStory: 'Retired firefighter and bush band drummer. Loves community events.', goals: [{ activity: 'Social', target: 3, current: 3, period: 'week' }, { activity: 'Exercise', target: 3, current: 2, period: 'week' }] },
];

const mockActivities = [
  { id: 'A001', name: 'Morning Tai Chi', type: 'Exercise', day: 'Monday', time: '8:00', duration: 45, location: 'Garden Courtyard', facilitator: 'Lisa Chen', maxCapacity: 15, description: 'Gentle tai chi focusing on balance and breathing.', participants: ['R002', 'R005', 'R008', 'R010'] },
  { id: 'A002', name: 'Watercolour Painting', type: 'Art', day: 'Monday', time: '10:00', duration: 90, location: 'Arts & Crafts Room', facilitator: 'Sarah Wilson', maxCapacity: 12, description: 'Guided watercolour session with seasonal themes.', participants: ['R003', 'R007', 'R001'] },
  { id: 'A003', name: 'Piano Singalong', type: 'Music', day: 'Monday', time: '14:00', duration: 60, location: 'Common Room', facilitator: 'David Brown', maxCapacity: 25, description: 'Classic songs from the 40s\u201360s with live piano.', participants: ['R001', 'R004', 'R006', 'R009', 'R010'] },
  { id: 'A004', name: 'Morning Walk Group', type: 'Exercise', day: 'Tuesday', time: '9:00', duration: 30, location: 'Garden Path', facilitator: 'Emma Davis', maxCapacity: 10, description: 'Supervised gentle walk around the facility grounds.', participants: ['R002', 'R005', 'R008', 'R010'] },
  { id: 'A005', name: 'Brain Games & Puzzles', type: 'Cognitive', day: 'Tuesday', time: '11:00', duration: 60, location: 'Library', facilitator: 'Robert Taylor', maxCapacity: 10, description: 'Crosswords, Sudoku, trivia and word games.', participants: ['R002', 'R006', 'R008'] },
  { id: 'A006', name: 'Chapel Service', type: 'Spiritual', day: 'Tuesday', time: '15:00', duration: 45, location: 'Chapel', facilitator: 'Rev. Mary Andrews', maxCapacity: 30, description: 'Non-denominational spiritual gathering with hymns.', participants: ['R003', 'R006', 'R009'] },
  { id: 'A007', name: 'Garden Club', type: 'Gardening', day: 'Wednesday', time: '10:00', duration: 90, location: 'Sensory Garden', facilitator: 'Tom Mitchell', maxCapacity: 8, description: 'Seasonal planting, herb care, and flower arranging.', participants: ['R001', 'R003', 'R005', 'R009'] },
  { id: 'A008', name: 'Social Bingo', type: 'Social', day: 'Wednesday', time: '14:00', duration: 90, location: 'Main Hall', facilitator: 'Jenny Smith', maxCapacity: 30, description: 'Bingo with prizes, afternoon tea included.', participants: ['R004', 'R005', 'R007', 'R010', 'R001', 'R002'] },
  { id: 'A009', name: 'Gentle Yoga', type: 'Exercise', day: 'Thursday', time: '9:00', duration: 45, location: 'Activity Room B', facilitator: 'Lisa Chen', maxCapacity: 12, description: 'Chair-based yoga for flexibility and relaxation.', participants: ['R002', 'R003', 'R005', 'R008'] },
  { id: 'A010', name: 'Music Therapy', type: 'Music', day: 'Thursday', time: '11:00', duration: 60, location: 'Therapy Room', facilitator: 'David Brown', maxCapacity: 8, description: 'One-on-one and small group therapeutic music sessions.', participants: ['R001', 'R006', 'R009'] },
  { id: 'A011', name: 'Pottery & Clay', type: 'Art', day: 'Thursday', time: '14:00', duration: 90, location: 'Arts & Crafts Room', facilitator: 'Sarah Wilson', maxCapacity: 10, description: 'Hand-building pottery and clay modelling.', participants: ['R003', 'R007'] },
  { id: 'A012', name: 'Meditation Circle', type: 'Spiritual', day: 'Friday', time: '8:00', duration: 30, location: 'Quiet Room', facilitator: 'Rev. Mary Andrews', maxCapacity: 15, description: 'Guided mindfulness and breathing meditation.', participants: ['R003', 'R006', 'R009'] },
  { id: 'A013', name: 'Friday Film Club', type: 'Social', day: 'Friday', time: '14:00', duration: 120, location: 'Theatre Room', facilitator: 'Jenny Smith', maxCapacity: 20, description: 'Classic movie screening with popcorn and discussion.', participants: ['R004', 'R007', 'R010', 'R001', 'R008'] },
  { id: 'A014', name: 'Shopping Outing', type: 'Outing', day: 'Friday', time: '10:00', duration: 180, location: 'Local Shopping Centre', facilitator: 'Emma Davis', maxCapacity: 6, description: 'Supervised trip to the local shops with wheelchair access bus.', participants: ['R004', 'R008'] },
  { id: 'A015', name: 'Saturday Concert', type: 'Music', day: 'Saturday', time: '15:00', duration: 90, location: 'Main Hall', facilitator: 'External Performer', maxCapacity: 40, description: 'Live performance by local community musicians.', participants: ['R001', 'R004', 'R006', 'R009', 'R010', 'R007'] },
  { id: 'A016', name: 'Craft Market Prep', type: 'Art', day: 'Saturday', time: '10:00', duration: 120, location: 'Arts & Crafts Room', facilitator: 'Sarah Wilson', maxCapacity: 12, description: 'Creating items for the quarterly craft market fundraiser.', participants: ['R003', 'R007', 'R001'] },
  { id: 'A017', name: 'Sunday Roast Social', type: 'Social', day: 'Sunday', time: '12:00', duration: 120, location: 'Dining Hall', facilitator: 'Kitchen Team', maxCapacity: 40, description: 'Traditional Sunday roast with family visiting encouraged.', participants: ['R001', 'R002', 'R004', 'R005', 'R007', 'R010'] },
  { id: 'A018', name: 'Chess & Board Games', type: 'Cognitive', day: 'Wednesday', time: '16:00', duration: 60, location: 'Library', facilitator: 'Robert Taylor', maxCapacity: 8, description: 'Chess, draughts, Scrabble and card games.', participants: ['R002', 'R006', 'R008'] },
];

const mockParticipationHistory = [
  { activityId: 'A003', date: '2026-03-31', attendees: [
    { residentId: 'R001', engagement: 'Highly Engaged', notes: 'Led the singing, very enthusiastic' },
    { residentId: 'R004', engagement: 'Engaged', notes: 'Enjoyed jazz numbers' },
    { residentId: 'R006', engagement: 'Engaged', notes: 'Tapped along, smiled throughout' },
    { residentId: 'R009', engagement: 'Highly Engaged', notes: 'Requested favourite hymns' },
    { residentId: 'R010', engagement: 'Passive', notes: 'Sat quietly, seemed tired' },
  ]},
  { activityId: 'A008', date: '2026-04-02', attendees: [
    { residentId: 'R004', engagement: 'Highly Engaged', notes: 'Won two rounds, very competitive' },
    { residentId: 'R005', engagement: 'Engaged', notes: 'Good social interaction with table' },
    { residentId: 'R007', engagement: 'Highly Engaged', notes: 'Helped organise prizes' },
    { residentId: 'R010', engagement: 'Engaged', notes: 'Enjoyed afternoon tea' },
    { residentId: 'R001', engagement: 'Engaged', notes: 'Chatted with visitors' },
    { residentId: 'R002', engagement: 'Passive', notes: 'Watched but didn\'t play much' },
  ]},
  { activityId: 'A001', date: '2026-04-07', attendees: [
    { residentId: 'R002', engagement: 'Highly Engaged', notes: 'Excellent form, very focused' },
    { residentId: 'R005', engagement: 'Engaged', notes: 'Good balance improvements noted' },
    { residentId: 'R008', engagement: 'Highly Engaged', notes: 'Helped newcomers with positions' },
    { residentId: 'R010', engagement: 'Engaged', notes: 'Completed full session' },
  ]},
  { activityId: 'A007', date: '2026-04-02', attendees: [
    { residentId: 'R001', engagement: 'Engaged', notes: 'Planted herbs, enjoyed the sunshine' },
    { residentId: 'R003', engagement: 'Highly Engaged', notes: 'Took charge of flower arranging' },
    { residentId: 'R005', engagement: 'Highly Engaged', notes: 'Weeded vegetable patch enthusiastically' },
    { residentId: 'R009', engagement: 'Engaged', notes: 'Harvested rosemary for teas' },
  ]},
];

const mockVolunteers = [
  { id: 'V001', name: 'Sarah Mitchell', skills: ['Music', 'Art'], availability: 'Mon, Wed, Fri mornings', phone: '0412 345 678', email: 'sarah.m@email.com', policeCheck: 'Current', policeCheckExpiry: '2027-03-15', wwcc: 'Current', wwccExpiry: '2027-06-20', totalHours: 245, lastVisit: '2026-04-07', status: 'Active' },
  { id: 'V002', name: 'Tom Bradley', skills: ['Gardening', 'Exercise'], availability: 'Tue, Thu all day', phone: '0423 456 789', email: 'tom.b@email.com', policeCheck: 'Current', policeCheckExpiry: '2026-11-01', wwcc: 'Current', wwccExpiry: '2027-01-10', totalHours: 182, lastVisit: '2026-04-08', status: 'Active' },
  { id: 'V003', name: 'Maria Gonzalez', skills: ['Social', 'Spiritual'], availability: 'Sat, Sun afternoons', phone: '0434 567 890', email: 'maria.g@email.com', policeCheck: 'Current', policeCheckExpiry: '2027-08-22', wwcc: 'Current', wwccExpiry: '2027-09-30', totalHours: 98, lastVisit: '2026-04-06', status: 'Active' },
  { id: 'V004', name: 'Kevin O\'Reilly', skills: ['Cognitive', 'Social'], availability: 'Wed afternoons', phone: '0445 678 901', email: 'kevin.o@email.com', policeCheck: 'Current', policeCheckExpiry: '2026-07-15', wwcc: 'Expiring Soon', wwccExpiry: '2026-05-01', totalHours: 67, lastVisit: '2026-04-02', status: 'Active' },
  { id: 'V005', name: 'Jenny Liu', skills: ['Art', 'Cognitive'], availability: 'Mon, Fri afternoons', phone: '0456 789 012', email: 'jenny.l@email.com', policeCheck: 'Current', policeCheckExpiry: '2027-02-28', wwcc: 'Current', wwccExpiry: '2027-04-15', totalHours: 156, lastVisit: '2026-04-04', status: 'Active' },
  { id: 'V006', name: 'David Park', skills: ['Exercise', 'Outing'], availability: 'Tue, Sat mornings', phone: '0467 890 123', email: 'david.p@email.com', policeCheck: 'Expired', policeCheckExpiry: '2026-02-10', wwcc: 'Current', wwccExpiry: '2027-05-20', totalHours: 34, lastVisit: '2026-03-15', status: 'Inactive' },
  { id: 'V007', name: 'Ruth Henderson', skills: ['Music', 'Spiritual'], availability: 'Thu, Sun mornings', phone: '0478 901 234', email: 'ruth.h@email.com', policeCheck: 'Current', policeCheckExpiry: '2027-07-01', wwcc: 'Current', wwccExpiry: '2027-08-15', totalHours: 312, lastVisit: '2026-04-09', status: 'Active' },
  { id: 'V008', name: 'Alan Wong', skills: ['Social', 'Gardening'], availability: 'Wed, Fri all day', phone: '0489 012 345', email: 'alan.w@email.com', policeCheck: 'Current', policeCheckExpiry: '2026-12-20', wwcc: 'Current', wwccExpiry: '2027-02-28', totalHours: 128, lastVisit: '2026-04-05', status: 'Active' },
];

const mockCouncilMeetings = [
  {
    id: 'CM001', date: '2026-04-03', time: '14:00', location: 'Board Room', chairperson: 'Margaret Thompson',
    agenda: ['Welcome and apologies', 'Review of March action items', 'Menu feedback discussion', 'Garden upgrade proposal', 'Activity schedule changes', 'General business'],
    minutes: 'Meeting opened at 2:00 PM. Margaret welcomed 8 resident representatives. March action items reviewed: ramp installation completed, new menu items trialled. Menu feedback: residents requested more soup options in winter. Garden upgrade: proposal for sensory garden extension approved in principle \u2014 management to provide costings. Activity schedule: Saturday concert time moved to 3 PM by popular request. General business: request for library book rotation and a monthly newsletter.',
    actionItems: [
      { id: 'AI001', description: 'Provide costings for sensory garden extension', assignee: 'Facility Manager', dueDate: '2026-04-17', status: 'In Progress' },
      { id: 'AI002', description: 'Add winter soup options to menu rotation', assignee: 'Kitchen Manager', dueDate: '2026-04-10', status: 'Completed' },
      { id: 'AI003', description: 'Arrange library book rotation with local library', assignee: 'Activities Coordinator', dueDate: '2026-04-24', status: 'Pending' },
      { id: 'AI004', description: 'Draft monthly newsletter template', assignee: 'Admin Team', dueDate: '2026-04-30', status: 'Pending' },
    ],
    attendees: ['Margaret Thompson', 'Harold Stevens', 'Dorothy Chen', 'Frank Morrison', 'Betty Williams', 'Rose McCarthy', 'James O\'Brien', 'Edna Kowalski'],
  },
  {
    id: 'CM002', date: '2026-03-06', time: '14:00', location: 'Board Room', chairperson: 'Harold Stevens',
    agenda: ['Welcome', 'February action items', 'Ramp installation update', 'New activities proposal', 'Visitor parking concerns', 'General business'],
    minutes: 'Meeting opened at 2:05 PM. Harold chaired in Margaret\'s absence. February actions reviewed: pet therapy visit arranged successfully, Wi-Fi upgrade completed. Ramp installation: contractor confirmed start date of March 15. New activities: residents expressed strong interest in a cooking class and a book club. Visitor parking: management acknowledged the issue and will look at overflow options. General business: request for better outdoor lighting near the garden path.',
    actionItems: [
      { id: 'AI005', description: 'Arrange cooking class with kitchen team', assignee: 'Activities Coordinator', dueDate: '2026-03-20', status: 'Completed' },
      { id: 'AI006', description: 'Start weekly book club', assignee: 'Volunteer Coordinator', dueDate: '2026-03-27', status: 'Completed' },
      { id: 'AI007', description: 'Review visitor parking overflow options', assignee: 'Facility Manager', dueDate: '2026-04-03', status: 'In Progress' },
      { id: 'AI008', description: 'Improve garden path outdoor lighting', assignee: 'Maintenance', dueDate: '2026-03-31', status: 'Completed' },
    ],
    attendees: ['Harold Stevens', 'Dorothy Chen', 'Frank Morrison', 'Betty Williams', 'Arthur Nguyen', 'Rose McCarthy', 'George Patel'],
  },
  {
    id: 'CM003', date: '2026-02-06', time: '14:00', location: 'Board Room', chairperson: 'Margaret Thompson',
    agenda: ['Welcome', 'January action items', 'Summer activity review', 'Air conditioning concerns', 'Pet therapy proposal', 'General business'],
    minutes: 'Meeting opened at 2:00 PM with 9 resident representatives present. January actions: Christmas concert feedback overwhelmingly positive, request for quarterly events. Summer activities reviewed: water activities and outdoor BBQs well received. Air conditioning: several rooms reported inconsistent cooling \u2014 maintenance to audit all units. Pet therapy: strong support from residents, proposal approved for fortnightly visits. General business: suggestion for a resident noticeboard for sharing photos and messages.',
    actionItems: [
      { id: 'AI009', description: 'Audit air conditioning units in all wings', assignee: 'Maintenance', dueDate: '2026-02-20', status: 'Completed' },
      { id: 'AI010', description: 'Arrange fortnightly pet therapy visits', assignee: 'Activities Coordinator', dueDate: '2026-02-28', status: 'Completed' },
      { id: 'AI011', description: 'Install resident noticeboard in common area', assignee: 'Maintenance', dueDate: '2026-03-15', status: 'Completed' },
      { id: 'AI012', description: 'Plan quarterly community event calendar', assignee: 'Activities Coordinator', dueDate: '2026-03-01', status: 'Completed' },
    ],
    attendees: ['Margaret Thompson', 'Harold Stevens', 'Dorothy Chen', 'Frank Morrison', 'Betty Williams', 'Arthur Nguyen', 'Edna Kowalski', 'George Patel', 'James O\'Brien'],
  },
];

function TrendLineChart({ data, height = 160 }) {
  const maxVal = Math.max(...data.map(d => d.value));
  const minVal = Math.min(...data.map(d => d.value));
  const range = maxVal - minVal || 1;
  const pad = { top: 24, right: 24, bottom: 32, left: 44 };
  const svgW = 520;
  const svgH = height;
  const plotW = svgW - pad.left - pad.right;
  const plotH = svgH - pad.top - pad.bottom;

  const pts = data.map((d, i) => ({
    x: pad.left + (data.length > 1 ? (i / (data.length - 1)) * plotW : plotW / 2),
    y: pad.top + plotH - ((d.value - minVal) / range) * plotH,
    ...d
  }));

  const line = pts.map(p => `${p.x},${p.y}`).join(' ');
  const area = `${pts[0].x},${pad.top + plotH} ${line} ${pts[pts.length - 1].x},${pad.top + plotH}`;

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.25} />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
        const y = pad.top + plotH - pct * plotH;
        const val = Math.round(minVal + pct * range);
        return (
          <g key={i}>
            <line x1={pad.left} y1={y} x2={pad.left + plotW} y2={y} stroke="#e5e7eb" strokeWidth={1} />
            <text x={pad.left - 8} y={y + 4} textAnchor="end" fill="#9ca3af" fontSize={10}>{val}</text>
          </g>
        );
      })}
      <polygon points={area} fill="url(#trendGrad)" />
      <polyline points={line} fill="none" stroke="#8b5cf6" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={5} fill="white" stroke="#8b5cf6" strokeWidth={2.5} />
          <text x={p.x} y={pad.top + plotH + 18} textAnchor="middle" fill="#6b7280" fontSize={10}>{p.label}</text>
          <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#6b7280" fontSize={10} fontWeight="600">{p.value}</text>
        </g>
      ))}
    </svg>
  );
}

function ProgressRing({ value, max, size = 56, strokeWidth = 5 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <span className="absolute text-xs font-bold" style={{ color }}>{Math.round(pct)}%</span>
    </div>
  );
}

function BarChart({ data, maxValue, height = 120 }) {
  const barWidth = Math.max(24, Math.floor(200 / data.length));
  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {data.map((item, i) => {
        const barH = maxValue > 0 ? (item.value / maxValue) * (height - 20) : 0;
        return (
          <div key={i} className="flex flex-col items-center gap-1">
            <span className="text-xs text-gray-500 font-medium">{item.value}</span>
            <div
              className={`rounded-t ${item.color || 'bg-purple-400'} transition-all duration-300`}
              style={{ width: barWidth, height: Math.max(4, barH) }}
              title={`${item.label}: ${item.value}`}
            />
            <span className="text-xs text-gray-500 truncate" style={{ maxWidth: barWidth + 8 }}>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        active
          ? 'bg-purple-600 text-white shadow-md'
          : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
      }`}
    >
      <Icon size={16} />
      <span className="hidden md:inline">{label}</span>
      {count !== undefined && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function Badge({ children, className = '' }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{children}</span>;
}

function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>{children}</div>;
}

function ActivityCalendarTab() {
  const { showToast } = useToast();
  const [viewMode, setViewMode] = useState('week');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const filteredActivities = useMemo(() => {
    if (filterType === 'all') return mockActivities;
    return mockActivities.filter(a => a.type === filterType);
  }, [filterType]);

  const activitiesByDay = useMemo(() => {
    const map = {};
    DAYS.forEach(d => { map[d] = []; });
    filteredActivities.forEach(a => { if (map[a.day]) map[a.day].push(a); });
    return map;
  }, [filteredActivities]);

  const handleSaveActivity = () => {
    setShowNewForm(false);
    showToast('Activity created successfully', 'success');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${viewMode === 'week' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            Week View
          </button>
          <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${viewMode === 'list' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            List View
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="pl-8 pr-3 py-1.5 border rounded-lg text-sm bg-white appearance-none cursor-pointer"
            >
              <option value="all">All Types</option>
              {Object.keys(ACTIVITY_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={() => setShowNewForm(!showNewForm)} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors">
            <Plus size={14} /> New Activity
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(ACTIVITY_TYPES).map(([type, cfg]) => (
          <Badge key={type} className={cfg.light}>
            <cfg.icon size={12} className="mr-1" /> {type}
          </Badge>
        ))}
      </div>

      {showNewForm && (
        <Card className="p-4 border-purple-200 bg-purple-50/30">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Plus size={16} className="text-purple-600" /> Schedule New Activity</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Activity name" className="px-3 py-2 border rounded-lg text-sm" />
            <select className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">Select type...</option>
              {Object.keys(ACTIVITY_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">Select day...</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">Select time...</option>
              {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <input type="number" placeholder="Duration (mins)" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Location" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Facilitator" className="px-3 py-2 border rounded-lg text-sm" />
            <input type="number" placeholder="Max capacity" className="px-3 py-2 border rounded-lg text-sm" />
            <textarea placeholder="Description" className="px-3 py-2 border rounded-lg text-sm md:col-span-3" rows={2} />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSaveActivity} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"><Save size={14} /> Save Activity</button>
            <button onClick={() => setShowNewForm(false)} className="px-4 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100">Cancel</button>
          </div>
        </Card>
      )}

      {viewMode === 'week' ? (
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">
          {DAYS.map(day => (
            <Card key={day} className="p-3">
              <h4 className="font-semibold text-sm text-gray-700 mb-2 pb-2 border-b">{day}</h4>
              <div className="space-y-2">
                {activitiesByDay[day].sort((a, b) => a.time.localeCompare(b.time)).map(activity => {
                  const cfg = ACTIVITY_TYPES[activity.type] || {};
                  return (
                    <button
                      key={activity.id}
                      onClick={() => setSelectedActivity(selectedActivity?.id === activity.id ? null : activity)}
                      className={`w-full text-left p-2 rounded-lg border-l-4 hover:shadow-md transition-all cursor-pointer ${
                        selectedActivity?.id === activity.id ? 'ring-2 ring-purple-400 bg-purple-50' : 'bg-gray-50'
                      }`}
                      style={{ borderLeftColor: cfg.color ? undefined : '#9333ea' }}
                    >
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-0.5">
                          <Clock size={10} /> {activity.time} · {activity.duration}m
                        </div>
                        <p className="text-sm font-medium text-gray-800 leading-tight">{activity.name}</p>
                        <Badge className={`mt-1 ${cfg.light || 'bg-gray-100 text-gray-600'}`}>
                          {activity.type}
                        </Badge>
                      </div>
                    </button>
                  );
                })}
                {activitiesByDay[day].length === 0 && (
                  <p className="text-xs text-gray-400 italic py-2">No activities</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          {filteredActivities.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No activities scheduled"
              description="No activities match the selected filter. Try 'All Types' or add a new activity."
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Activity', 'Type', 'Day', 'Time', 'Duration', 'Location', 'Facilitator', 'Capacity'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredActivities.map(a => {
                  const cfg = ACTIVITY_TYPES[a.type] || {};
                  return (
                    <tr key={a.id} className="hover:bg-purple-50/30 cursor-pointer" onClick={() => setSelectedActivity(selectedActivity?.id === a.id ? null : a)}>
                      <td className="px-3 py-2.5 font-medium text-gray-800">{a.name}</td>
                      <td className="px-3 py-2.5"><Badge className={cfg.light}>{a.type}</Badge></td>
                      <td className="px-3 py-2.5 text-gray-600">{a.day}</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.time}</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.duration}m</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.location}</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.facilitator}</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.participants.length}/{a.maxCapacity}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {selectedActivity && (
        <Card className="p-5 border-purple-200">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-lg font-bold text-gray-800">{selectedActivity.name}</h3>
              <Badge className={ACTIVITY_TYPES[selectedActivity.type]?.light || 'bg-gray-100'}>
                {selectedActivity.type}
              </Badge>
            </div>
            <div className="flex gap-2">
              <button className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100">
                <Eye size={14} /> Push to Family Portal
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100">
                <Edit size={14} /> Edit
              </button>
              <button onClick={() => setSelectedActivity(null)} className="p-1.5 text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Day/Time</span><p className="font-medium">{selectedActivity.day} {selectedActivity.time}</p></div>
            <div><span className="text-gray-500">Duration</span><p className="font-medium">{selectedActivity.duration} minutes</p></div>
            <div><span className="text-gray-500">Location</span><p className="font-medium">{selectedActivity.location}</p></div>
            <div><span className="text-gray-500">Facilitator</span><p className="font-medium">{selectedActivity.facilitator}</p></div>
          </div>
          <p className="mt-3 text-sm text-gray-600">{selectedActivity.description}</p>
          <div className="mt-3">
            <span className="text-sm text-gray-500">Participants ({selectedActivity.participants.length}/{selectedActivity.maxCapacity})</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {selectedActivity.participants.map(pid => {
                const r = mockResidents.find(res => res.id === pid);
                return r ? <Badge key={pid} className="bg-purple-100 text-purple-700">{r.name}</Badge> : null;
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function ResidentPlansTab() {
  const { showToast } = useToast();
  const [selectedResidentId, setSelectedResidentId] = useState(mockResidents[0].id);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredResidents = useMemo(() => {
    if (!searchQuery) return mockResidents;
    const q = searchQuery.toLowerCase();
    return mockResidents.filter(r => r.name.toLowerCase().includes(q) || r.interests.some(i => i.toLowerCase().includes(q)));
  }, [searchQuery]);

  const selectedResident = mockResidents.find(r => r.id === selectedResidentId);

  const residentActivities = useMemo(() => {
    if (!selectedResident) return [];
    return mockActivities.filter(a => a.participants.includes(selectedResident.id));
  }, [selectedResident]);

  const recommendedActivities = useMemo(() => {
    if (!selectedResident) return [];
    return mockActivities.filter(a =>
      !a.participants.includes(selectedResident.id) &&
      selectedResident.interests.includes(a.type) &&
      a.participants.length < a.maxCapacity
    );
  }, [selectedResident]);

  const handleUpdateGoal = () => {
    showToast('Goal progress updated', 'success');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-4 lg:col-span-1">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Users size={16} className="text-purple-600" /> Residents</h3>
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search residents or interests..."
            className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          {filteredResidents.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No residents found"
              description="No residents match your search."
            />
          ) : (
            filteredResidents.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedResidentId(r.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                  selectedResidentId === r.id ? 'bg-purple-100 text-purple-800' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-gray-500">Room {r.room}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {r.interests.map(i => (
                    <Badge key={i} className={ACTIVITY_TYPES[i]?.light || 'bg-gray-100 text-gray-600'}>{i}</Badge>
                  ))}
                </div>
              </button>
            ))
          )}
        </div>
      </Card>

      {selectedResident && (
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">{selectedResident.name}</h3>
                <p className="text-sm text-gray-500">Room {selectedResident.room}</p>
              </div>
              <button className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100">
                <Edit size={14} /> Edit Plan
              </button>
            </div>
            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><BookOpen size={14} /> My Life Story</h4>
              <p className="text-sm text-amber-700 mt-1">{selectedResident.lifeStory}</p>
            </div>
            <div className="mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Interests</h4>
              <div className="flex flex-wrap gap-2">
                {selectedResident.interests.map(i => {
                  const cfg = ACTIVITY_TYPES[i] || {};
                  const TypeIcon = cfg.icon || Star;
                  return (
                    <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${cfg.light || 'bg-gray-100 text-gray-700'}`}>
                      <TypeIcon size={14} /> {i}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2"><Target size={16} className="text-purple-600" /> Activity Goals</h4>
              <Tooltip content="Goals track how many activities of each type the resident aims to attend per week/month">
                <span className="text-xs text-gray-400 cursor-help flex items-center gap-1"><AlertCircle size={12} /> Scoring info</span>
              </Tooltip>
            </div>
            <div className="space-y-3">
              {selectedResident.goals.map((goal, i) => {
                const pct = goal.target > 0 ? Math.min(100, (goal.current / goal.target) * 100) : 0;
                const met = goal.current >= goal.target;
                return (
                  <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <ProgressRing value={goal.current} max={goal.target} size={52} strokeWidth={5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <Tooltip content={`${goal.current} of ${goal.target} ${goal.activity} activities completed this ${goal.period}`}>
                          <span className="text-sm font-medium text-gray-800">
                            {goal.target} {goal.activity} / {goal.period}
                          </span>
                        </Tooltip>
                        <Badge className={met ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                          {met ? <><CheckCircle size={12} className="mr-1" /> Met</> : <>{goal.current}/{goal.target}</>}
                        </Badge>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all ${met ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <button onClick={handleUpdateGoal} className="text-xs text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50">
                      <Edit size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Calendar size={16} className="text-purple-600" /> Current Activities ({residentActivities.length})</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {residentActivities.map(a => {
                const cfg = ACTIVITY_TYPES[a.type] || {};
                return (
                  <div key={a.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-8 rounded-full ${cfg.color || 'bg-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{a.name}</p>
                      <p className="text-xs text-gray-500">{a.day} {a.time} · {a.location}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {recommendedActivities.length > 0 && (
            <Card className="p-5 border-green-200 bg-green-50/30">
              <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2"><Star size={16} /> Recommended Activities</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {recommendedActivities.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-green-100">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{a.name}</p>
                      <p className="text-xs text-gray-500">{a.day} {a.time} · {a.type}</p>
                    </div>
                    <button className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200">+ Add</button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function ParticipationTab() {
  const { showToast } = useToast();
  const [selectedSession, setSelectedSession] = useState(null);
  const [trackingActivity, setTrackingActivity] = useState('');
  const [attendanceData, setAttendanceData] = useState({});

  const startTracking = (activityId) => {
    const activity = mockActivities.find(a => a.id === activityId);
    if (!activity) return;
    const initial = {};
    mockResidents.forEach(r => {
      initial[r.id] = { attended: activity.participants.includes(r.id), engagement: 'Engaged', notes: '' };
    });
    setAttendanceData(initial);
    setTrackingActivity(activityId);
  };

  const toggleAttendance = (residentId) => {
    setAttendanceData(prev => ({
      ...prev,
      [residentId]: { ...prev[residentId], attended: !prev[residentId].attended }
    }));
  };

  const updateEngagement = (residentId, level) => {
    setAttendanceData(prev => ({
      ...prev,
      [residentId]: { ...prev[residentId], engagement: level }
    }));
  };

  const updateNotes = (residentId, notes) => {
    setAttendanceData(prev => ({
      ...prev,
      [residentId]: { ...prev[residentId], notes }
    }));
  };

  const handleSaveParticipation = () => {
    showToast('Participation record saved', 'success');
  };

  const trackingActivityObj = mockActivities.find(a => a.id === trackingActivity);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><ClipboardList size={16} className="text-purple-600" /> Record Participation</h3>
          <p className="text-sm text-gray-500 mb-3">Select an activity session to record attendance and engagement.</p>
          <select
            value={trackingActivity}
            onChange={e => startTracking(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm bg-white mb-3"
          >
            <option value="">Choose an activity...</option>
            {mockActivities.map(a => (
              <option key={a.id} value={a.id}>{a.name} \u2014 {a.day} {a.time}</option>
            ))}
          </select>
          {trackingActivityObj && (
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="font-medium text-purple-800">{trackingActivityObj.name}</p>
              <p className="text-sm text-purple-600">{trackingActivityObj.day} {trackingActivityObj.time} · {trackingActivityObj.location}</p>
              <p className="text-sm text-purple-600">Facilitator: {trackingActivityObj.facilitator}</p>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-purple-600" /> Quick Stats</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">{mockParticipationHistory.length}</p>
              <p className="text-xs text-green-600">Sessions Recorded</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700">
                {mockParticipationHistory.reduce((sum, s) => sum + s.attendees.length, 0)}
              </p>
              <p className="text-xs text-blue-600">Total Attendances</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-700">
                {Math.round(mockParticipationHistory.reduce((sum, s) => sum + s.attendees.filter(a => a.engagement === 'Highly Engaged').length, 0) / Math.max(1, mockParticipationHistory.reduce((sum, s) => sum + s.attendees.length, 0)) * 100)}%
              </p>
              <p className="text-xs text-purple-600">Highly Engaged</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-700">{mockResidents.length}</p>
              <p className="text-xs text-amber-600">Active Residents</p>
            </div>
          </div>
        </Card>
      </div>

      {trackingActivity && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Attendance \u2014 {trackingActivityObj?.name}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Resident</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Attended</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Engagement</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mockResidents.map(r => {
                  const data = attendanceData[r.id] || { attended: false, engagement: 'Absent', notes: '' };
                  return (
                    <tr key={r.id} className={data.attended ? 'bg-green-50/30' : ''}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-800">{r.name}</p>
                        <p className="text-xs text-gray-500">Room {r.room}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => toggleAttendance(r.id)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            data.attended ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-purple-400'
                          }`}
                        >
                          {data.attended && <CheckCircle size={14} />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <Tooltip content={ENGAGEMENT_TIPS[data.engagement] || ''}>
                          <select
                            value={data.engagement}
                            onChange={e => updateEngagement(r.id, e.target.value)}
                            className="px-2 py-1 border rounded text-xs bg-white"
                            disabled={!data.attended}
                          >
                            {ENGAGEMENT_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </Tooltip>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={data.notes}
                          onChange={e => updateNotes(r.id, e.target.value)}
                          placeholder="Facilitator notes..."
                          className="w-full px-2 py-1 border rounded text-xs"
                          disabled={!data.attended}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSaveParticipation} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
              <Save size={14} /> Save Participation Record
            </button>
          </div>
        </Card>
      )}

      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Clock size={16} className="text-purple-600" /> Recent Sessions</h3>
        <div className="space-y-3">
          {mockParticipationHistory.map((session, idx) => {
            const activity = mockActivities.find(a => a.id === session.activityId);
            const isOpen = selectedSession === idx;
            return (
              <div key={idx} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setSelectedSession(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Badge className={ACTIVITY_TYPES[activity?.type]?.light || 'bg-gray-100'}>
                      {activity?.type}
                    </Badge>
                    <div className="text-left">
                      <p className="font-medium text-sm text-gray-800">{activity?.name}</p>
                      <p className="text-xs text-gray-500">{session.date} · {session.attendees.length} attendees</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 border-t bg-gray-50/50">
                    <table className="w-full text-sm mt-2">
                      <thead>
                        <tr>
                          <th className="text-left text-xs font-semibold text-gray-500 pb-1">Resident</th>
                          <th className="text-left text-xs font-semibold text-gray-500 pb-1">Engagement</th>
                          <th className="text-left text-xs font-semibold text-gray-500 pb-1">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {session.attendees.map(att => {
                          const res = mockResidents.find(r => r.id === att.residentId);
                          return (
                            <tr key={att.residentId} className="border-t border-gray-100">
                              <td className="py-1.5 font-medium text-gray-700">{res?.name}</td>
                              <td className="py-1.5">
                                <Tooltip content={ENGAGEMENT_TIPS[att.engagement] || ''}>
                                  <Badge className={ENGAGEMENT_COLORS[att.engagement]}>{att.engagement}</Badge>
                                </Tooltip>
                              </td>
                              <td className="py-1.5 text-gray-600 text-xs">{att.notes}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function EvaluationTab() {
  const participationByType = useMemo(() => {
    const counts = {};
    Object.keys(ACTIVITY_TYPES).forEach(t => { counts[t] = 0; });
    mockActivities.forEach(a => { counts[a.type] = (counts[a.type] || 0) + a.participants.length; });
    return counts;
  }, []);

  const engagementScores = useMemo(() => {
    const scores = { 'Highly Engaged': 4, 'Engaged': 3, 'Passive': 2, 'Declined': 1, 'Absent': 0 };
    let totalScore = 0, totalCount = 0;
    mockParticipationHistory.forEach(s => {
      s.attendees.forEach(a => { totalScore += scores[a.engagement] || 0; totalCount++; });
    });
    return totalCount > 0 ? (totalScore / totalCount).toFixed(1) : 0;
  }, []);

  const activityPopularity = useMemo(() => {
    return [...mockActivities]
      .sort((a, b) => b.participants.length - a.participants.length)
      .slice(0, 5);
  }, []);

  const leastPopular = useMemo(() => {
    return [...mockActivities]
      .sort((a, b) => a.participants.length - b.participants.length)
      .slice(0, 3);
  }, []);

  const typeData = useMemo(() => {
    return Object.entries(participationByType).map(([type, count]) => ({
      label: type.substring(0, 4),
      value: count,
      color: ACTIVITY_TYPES[type]?.color?.replace('bg-', 'bg-') || 'bg-gray-400',
    }));
  }, [participationByType]);

  const totalParticipations = Object.values(participationByType).reduce((s, v) => s + v, 0);

  const residentEngagement = useMemo(() => {
    const map = {};
    mockResidents.forEach(r => { map[r.id] = { name: r.name, sessions: 0, totalScore: 0 }; });
    mockParticipationHistory.forEach(s => {
      s.attendees.forEach(a => {
        if (map[a.residentId]) {
          map[a.residentId].sessions++;
          const scores = { 'Highly Engaged': 4, 'Engaged': 3, 'Passive': 2, 'Declined': 1, 'Absent': 0 };
          map[a.residentId].totalScore += scores[a.engagement] || 0;
        }
      });
    });
    return Object.values(map).sort((a, b) => b.sessions - a.sessions);
  }, []);

  const monthlyTrend = [
    { label: 'Nov', value: 68 }, { label: 'Dec', value: 72 },
    { label: 'Jan', value: 75 }, { label: 'Feb', value: 78 },
    { label: 'Mar', value: 85 }, { label: 'Apr', value: 82 },
  ];

  const handlePrint = () => { window.print(); };

  const handleExportCSV = () => {
    const headers = ['Resident', 'Sessions Attended', 'Avg Score', 'Engagement Level'];
    const rows = residentEngagement.map(r => {
      const avg = r.sessions > 0 ? (r.totalScore / r.sessions) : 0;
      const level = avg >= 3.5 ? 'Excellent' : avg >= 2.5 ? 'Good' : avg >= 1.5 ? 'Fair' : r.sessions === 0 ? 'No Data' : 'Low';
      return [r.name, r.sessions, r.sessions > 0 ? avg.toFixed(1) : '0', level];
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wellbeing_report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div />
        <div className="flex items-center gap-2">
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors">
            <Printer size={14} /> Print Report
          </button>
          <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 text-center bg-gradient-to-br from-purple-50 to-white">
          <BarChart3 size={20} className="mx-auto text-purple-600 mb-1" />
          <p className="text-2xl font-bold text-purple-700">{totalParticipations}</p>
          <p className="text-xs text-gray-500">Total Participations</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-white">
          <Tooltip content="Average engagement score across all recorded sessions. Scale: Highly Engaged=4, Engaged=3, Passive=2, Declined=1, Absent=0">
            <div>
              <Star size={20} className="mx-auto text-blue-600 mb-1" />
              <p className="text-2xl font-bold text-blue-700">{engagementScores}</p>
              <p className="text-xs text-gray-500">Avg Engagement (of 4)</p>
            </div>
          </Tooltip>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-white">
          <Users size={20} className="mx-auto text-green-600 mb-1" />
          <p className="text-2xl font-bold text-green-700">{mockActivities.length}</p>
          <p className="text-xs text-gray-500">Weekly Activities</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-amber-50 to-white">
          <Award size={20} className="mx-auto text-amber-600 mb-1" />
          <p className="text-2xl font-bold text-amber-700">
            {Math.round(mockResidents.filter(r => mockActivities.some(a => a.participants.includes(r.id))).length / mockResidents.length * 100)}%
          </p>
          <p className="text-xs text-gray-500">Resident Participation</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-purple-600" /> Participation by Activity Type</h3>
          <BarChart data={typeData} maxValue={Math.max(...Object.values(participationByType), 1)} height={140} />
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2"><TrendingUp size={16} className="text-purple-600" /> Engagement Trend (6 Months)</h3>
            <Tooltip content="Monthly engagement score based on weighted participation, engagement levels, and resident feedback surveys">
              <span className="text-xs text-gray-400 cursor-help flex items-center gap-1"><AlertCircle size={12} /> Methodology</span>
            </Tooltip>
          </div>
          <TrendLineChart data={monthlyTrend} height={160} />
          <p className="text-xs text-gray-400 mt-2">Score out of 100 based on engagement and feedback surveys</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2"><TrendingUp size={16} /> Most Popular Activities</h3>
          <div className="space-y-2">
            {activityPopularity.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg bg-green-50/50">
                <span className="text-lg font-bold text-green-600 w-6">#{i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.type} · {a.day}</p>
                </div>
                <span className="text-sm font-semibold text-green-700">{a.participants.length}/{a.maxCapacity}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2"><AlertCircle size={16} /> Least Popular \u2014 Needs Attention</h3>
          <div className="space-y-2">
            {leastPopular.map(a => (
              <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg bg-orange-50/50">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.type} · {a.day} {a.time}</p>
                </div>
                <span className="text-sm font-semibold text-orange-700">{a.participants.length}/{a.maxCapacity}</span>
              </div>
            ))}
            <p className="text-xs text-gray-400 mt-2">Consider rescheduling, changing format, or promoting to residents.</p>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Users size={16} className="text-purple-600" /> Resident Engagement Summary</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Resident</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Sessions Attended</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Avg Score</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {residentEngagement.map(r => {
                const avg = r.sessions > 0 ? (r.totalScore / r.sessions) : 0;
                const level = avg >= 3.5 ? 'Excellent' : avg >= 2.5 ? 'Good' : avg >= 1.5 ? 'Fair' : r.sessions === 0 ? 'No Data' : 'Low';
                const levelColor = avg >= 3.5 ? 'bg-green-100 text-green-700' : avg >= 2.5 ? 'bg-blue-100 text-blue-700' : avg >= 1.5 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500';
                return (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{r.sessions}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{r.sessions > 0 ? avg.toFixed(1) : '\u2014'}</td>
                    <td className="px-3 py-2 text-center">
                      <Tooltip content={`${level}: avg ${r.sessions > 0 ? avg.toFixed(1) : 0}/4.0 across ${r.sessions} sessions`}>
                        <Badge className={levelColor}>{level}</Badge>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function VolunteersTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);

  const filteredVolunteers = useMemo(() => {
    if (!searchQuery) return mockVolunteers;
    const q = searchQuery.toLowerCase();
    return mockVolunteers.filter(v => v.name.toLowerCase().includes(q) || v.skills.some(s => s.toLowerCase().includes(q)));
  }, [searchQuery]);

  const totalHours = mockVolunteers.reduce((s, v) => s + v.totalHours, 0);
  const activeCount = mockVolunteers.filter(v => v.status === 'Active').length;
  const expiringChecks = mockVolunteers.filter(v => v.wwcc === 'Expiring Soon' || v.policeCheck === 'Expired').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 text-center bg-gradient-to-br from-purple-50 to-white">
          <UserPlus size={20} className="mx-auto text-purple-600 mb-1" />
          <p className="text-2xl font-bold text-purple-700">{mockVolunteers.length}</p>
          <p className="text-xs text-gray-500">Total Volunteers</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-white">
          <CheckCircle size={20} className="mx-auto text-green-600 mb-1" />
          <p className="text-2xl font-bold text-green-700">{activeCount}</p>
          <p className="text-xs text-gray-500">Active</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-white">
          <Clock size={20} className="mx-auto text-blue-600 mb-1" />
          <p className="text-2xl font-bold text-blue-700">{totalHours.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Total Hours</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-red-50 to-white">
          <AlertCircle size={20} className="mx-auto text-red-600 mb-1" />
          <p className="text-2xl font-bold text-red-700">{expiringChecks}</p>
          <p className="text-xs text-gray-500">Checks Expiring</p>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search volunteers or skills..."
            className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
          <UserPlus size={14} /> Add Volunteer
        </button>
      </div>

      {showAddForm && (
        <Card className="p-4 border-purple-200 bg-purple-50/30">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><UserPlus size={16} className="text-purple-600" /> New Volunteer</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Full name" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Phone" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Email" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Skills (comma separated)" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Availability" className="px-3 py-2 border rounded-lg text-sm" />
            <select className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">Police check status...</option>
              <option value="Current">Current</option>
              <option value="Pending">Pending</option>
              <option value="Expired">Expired</option>
            </select>
            <input type="date" placeholder="Police check expiry" className="px-3 py-2 border rounded-lg text-sm" />
            <select className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">WWCC status...</option>
              <option value="Current">Current</option>
              <option value="Pending">Pending</option>
              <option value="Expiring Soon">Expiring Soon</option>
              <option value="Expired">Expired</option>
            </select>
            <input type="date" placeholder="WWCC expiry" className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="flex gap-2 mt-3">
            <button className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"><Save size={14} /> Save Volunteer</button>
            <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100">Cancel</button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Skills', 'Availability', 'Contact', 'Police Check', 'WWCC', 'Hours', 'Last Visit', 'Status'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredVolunteers.map(v => (
              <tr key={v.id} className="hover:bg-purple-50/30 cursor-pointer" onClick={() => setSelectedVolunteer(selectedVolunteer?.id === v.id ? null : v)}>
                <td className="px-3 py-2.5 font-medium text-gray-800">{v.name}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {v.skills.map(s => <Badge key={s} className={ACTIVITY_TYPES[s]?.light || 'bg-gray-100 text-gray-600'}>{s}</Badge>)}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-gray-600 text-xs">{v.availability}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Phone size={10} /> {v.phone}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <Badge className={v.policeCheck === 'Current' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    <Shield size={10} className="mr-0.5" /> {v.policeCheck}
                  </Badge>
                </td>
                <td className="px-3 py-2.5">
                  <Badge className={v.wwcc === 'Current' ? 'bg-green-100 text-green-700' : v.wwcc === 'Expiring Soon' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                    {v.wwcc}
                  </Badge>
                </td>
                <td className="px-3 py-2.5 font-medium text-gray-700">{v.totalHours}h</td>
                <td className="px-3 py-2.5 text-gray-600 text-xs">{v.lastVisit}</td>
                <td className="px-3 py-2.5">
                  <Badge className={v.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {v.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {selectedVolunteer && (
        <Card className="p-5 border-purple-200">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800">{selectedVolunteer.name}</h3>
              <Badge className={selectedVolunteer.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                {selectedVolunteer.status}
              </Badge>
            </div>
            <button onClick={() => setSelectedVolunteer(null)} className="p-1.5 text-gray-400 hover:text-gray-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <div><span className="text-gray-500 flex items-center gap-1"><Phone size={12} /> Phone</span><p className="font-medium">{selectedVolunteer.phone}</p></div>
            <div><span className="text-gray-500 flex items-center gap-1"><Mail size={12} /> Email</span><p className="font-medium">{selectedVolunteer.email}</p></div>
            <div><span className="text-gray-500">Availability</span><p className="font-medium">{selectedVolunteer.availability}</p></div>
            <div><span className="text-gray-500">Total Hours</span><p className="font-medium">{selectedVolunteer.totalHours} hours</p></div>
            <div><span className="text-gray-500 flex items-center gap-1"><Shield size={12} /> Police Check</span><p className="font-medium">{selectedVolunteer.policeCheck} (exp. {selectedVolunteer.policeCheckExpiry})</p></div>
            <div><span className="text-gray-500">WWCC</span><p className="font-medium">{selectedVolunteer.wwcc} (exp. {selectedVolunteer.wwccExpiry})</p></div>
            <div><span className="text-gray-500">Skills</span><div className="flex flex-wrap gap-1 mt-0.5">{selectedVolunteer.skills.map(s => <Badge key={s} className={ACTIVITY_TYPES[s]?.light || 'bg-gray-100'}>{s}</Badge>)}</div></div>
            <div><span className="text-gray-500">Last Visit</span><p className="font-medium">{selectedVolunteer.lastVisit}</p></div>
          </div>
        </Card>
      )}
    </div>
  );
}

function CouncilTab() {
  const [selectedMeeting, setSelectedMeeting] = useState(mockCouncilMeetings[0].id);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [showNewAgendaItem, setShowNewAgendaItem] = useState(false);

  const meeting = mockCouncilMeetings.find(m => m.id === selectedMeeting);

  const allActionItems = mockCouncilMeetings.flatMap(m => m.actionItems);
  const pendingActions = allActionItems.filter(a => a.status === 'Pending' || a.status === 'In Progress');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><MessageSquare size={16} className="text-purple-600" /> Resident Council Meetings</h3>
        <button onClick={() => setShowNewMeeting(!showNewMeeting)} className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
          <Plus size={14} /> Schedule Meeting
        </button>
      </div>

      {showNewMeeting && (
        <Card className="p-4 border-purple-200 bg-purple-50/30">
          <h4 className="font-semibold text-gray-800 mb-3">Schedule New Meeting</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input type="date" className="px-3 py-2 border rounded-lg text-sm" />
            <input type="time" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Location" className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Chairperson" className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="flex gap-2 mt-3">
            <button className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"><Save size={14} /> Schedule</button>
            <button onClick={() => setShowNewMeeting(false)} className="px-4 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100">Cancel</button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-1">
          <h4 className="font-semibold text-sm text-gray-700 mb-3">Meeting History</h4>
          <div className="space-y-2">
            {mockCouncilMeetings.map(m => (
              <button
                key={m.id}
                onClick={() => setSelectedMeeting(m.id)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedMeeting === m.id ? 'bg-purple-100 text-purple-800' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <p className="font-medium text-sm">{m.date}</p>
                <p className="text-xs text-gray-500">Chaired by {m.chairperson}</p>
                <p className="text-xs text-gray-500">{m.attendees.length} attendees · {m.actionItems.length} actions</p>
              </button>
            ))}
          </div>
        </Card>

        {meeting && (
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Meeting \u2014 {meeting.date}</h3>
                  <p className="text-sm text-gray-500">{meeting.time} · {meeting.location} · Chair: {meeting.chairperson}</p>
                </div>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100">
                  <FileText size={14} /> Export Minutes
                </button>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Users size={14} /> Attendees</h4>
                <div className="flex flex-wrap gap-1.5">
                  {meeting.attendees.map(a => <Badge key={a} className="bg-purple-100 text-purple-700">{a}</Badge>)}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><ClipboardList size={14} /> Agenda</h4>
                  <button onClick={() => setShowNewAgendaItem(!showNewAgendaItem)} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"><Plus size={12} /> Add Item</button>
                </div>
                {showNewAgendaItem && (
                  <div className="flex gap-2 mb-2">
                    <input placeholder="New agenda item..." className="flex-1 px-3 py-1.5 border rounded-lg text-sm" />
                    <button className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">Add</button>
                  </div>
                )}
                <ol className="list-decimal list-inside space-y-1">
                  {meeting.agenda.map((item, i) => (
                    <li key={i} className="text-sm text-gray-700 py-1 px-2 rounded hover:bg-gray-50">{item}</li>
                  ))}
                </ol>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><FileText size={14} /> Minutes</h4>
                <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                  {meeting.minutes}
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Target size={16} className="text-purple-600" /> Action Items</h4>
              <div className="space-y-2">
                {meeting.actionItems.map(ai => (
                  <div key={ai.id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                    ai.status === 'Completed' ? 'bg-green-50 border-green-100' :
                    ai.status === 'In Progress' ? 'bg-blue-50 border-blue-100' :
                    'bg-gray-50 border-gray-100'
                  }`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      ai.status === 'Completed' ? 'bg-green-500' :
                      ai.status === 'In Progress' ? 'bg-blue-500' :
                      'bg-gray-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{ai.description}</p>
                      <p className="text-xs text-gray-500">Assigned to: {ai.assignee} · Due: {ai.dueDate}</p>
                    </div>
                    <Badge className={
                      ai.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      ai.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }>{ai.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {pendingActions.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50/30">
          <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2"><AlertCircle size={16} /> Outstanding Action Items ({pendingActions.length})</h4>
          <div className="space-y-2">
            {pendingActions.map(ai => (
              <div key={ai.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-100">
                <div>
                  <p className="text-sm font-medium text-gray-800">{ai.description}</p>
                  <p className="text-xs text-gray-500">{ai.assignee} · Due: {ai.dueDate}</p>
                </div>
                <Badge className={ai.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}>{ai.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function AlertsTab() {
  const [alertFilter, setAlertFilter] = useState('all');

  const activityAlerts = useMemo(() => {
    const alerts = [];
    mockActivities.forEach(a => {
      const fillRate = a.participants.length / a.maxCapacity;
      if (fillRate >= 0.85) {
        alerts.push({ id: `cap-${a.id}`, type: 'capacity', severity: 'medium', activity: a.name, message: `${Math.round(fillRate * 100)}% capacity \u2014 consider adding a session`, time: a.day + ' ' + a.time });
      }
      if (fillRate <= 0.25 && a.maxCapacity > 4) {
        alerts.push({ id: `low-${a.id}`, type: 'attendance', severity: 'low', activity: a.name, message: 'Low attendance \u2014 consider rescheduling or promoting', time: a.day + ' ' + a.time });
      }
    });
    const outdoorActivities = mockActivities.filter(a => a.type === 'Gardening' || a.type === 'Outing' || a.location.toLowerCase().includes('garden'));
    outdoorActivities.forEach(a => {
      alerts.push({ id: `wx-${a.id}`, type: 'weather', severity: 'medium', activity: a.name, message: 'Outdoor activity \u2014 check weather before session', time: a.day + ' ' + a.time });
    });
    const expiringVols = mockVolunteers.filter(v => v.wwcc === 'Expiring Soon' || v.policeCheck === 'Expired');
    expiringVols.forEach(v => {
      alerts.push({ id: `vol-${v.id}`, type: 'compliance', severity: 'high', activity: `Volunteer: ${v.name}`, message: v.policeCheck === 'Expired' ? 'Police check expired \u2014 cannot volunteer until renewed' : 'WWCC expiring soon \u2014 renewal required', time: 'Immediate' });
    });
    return alerts;
  }, []);

  const filtered = alertFilter === 'all' ? activityAlerts : activityAlerts.filter(a => a.type === alertFilter);
  const severityStyle = (s) => s === 'high' ? 'border-red-500 bg-red-50' : s === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50';
  const badgeStyle = (s) => s === 'high' ? 'bg-red-100 text-red-800' : s === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Bell size={16} className="text-purple-600" /> Activity & Wellbeing Alerts</h3>
        <div className="flex items-center gap-2">
          {['all', 'capacity', 'attendance', 'weather', 'compliance'].map(f => (
            <button key={f} onClick={() => setAlertFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${alertFilter === f ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'}`}>
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 text-center bg-gradient-to-br from-red-50 to-white">
          <p className="text-2xl font-bold text-red-700">{activityAlerts.filter(a => a.severity === 'high').length}</p>
          <p className="text-xs text-gray-500">Critical</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-yellow-50 to-white">
          <p className="text-2xl font-bold text-yellow-700">{activityAlerts.filter(a => a.severity === 'medium').length}</p>
          <p className="text-xs text-gray-500">Warnings</p>
        </Card>
        <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-white">
          <p className="text-2xl font-bold text-blue-700">{activityAlerts.filter(a => a.severity === 'low').length}</p>
          <p className="text-xs text-gray-500">Info</p>
        </Card>
      </div>
      <div className="space-y-2">
        {filtered.map(alert => (
          <Card key={alert.id} className={`p-4 border-l-4 ${severityStyle(alert.severity)}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-sm text-gray-800">{alert.activity}</p>
                  <Badge className={badgeStyle(alert.severity)}>{alert.severity}</Badge>
                  <Badge className="bg-gray-100 text-gray-600">{alert.type}</Badge>
                </div>
                <p className="text-sm text-gray-600">{alert.message}</p>
                <p className="text-xs text-gray-400 mt-1">{alert.time}</p>
              </div>
              <button className="text-xs px-2.5 py-1 bg-white border rounded-lg text-gray-500 hover:bg-gray-50">Dismiss</button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <EmptyState
            icon={Bell}
            title="No alerts"
            description="No alerts match the selected filter."
          />
        )}
      </div>
    </div>
  );
}

function AIInsightsTab() {
  const aiData = useMemo(() => {
    const totalParticipants = mockActivities.reduce((s, a) => s + a.participants.length, 0);
    const totalCapacity = mockActivities.reduce((s, a) => s + a.maxCapacity, 0);
    const attendanceRate = totalCapacity > 0 ? Math.round((totalParticipants / totalCapacity) * 100) : 0;

    const engagementScores = { 'Highly Engaged': 4, 'Engaged': 3, 'Passive': 2, 'Declined': 1, 'Absent': 0 };
    let totalScore = 0, totalCount = 0;
    mockParticipationHistory.forEach(s => {
      s.attendees.forEach(a => { totalScore += engagementScores[a.engagement] || 0; totalCount++; });
    });
    const avgEngagement = totalCount > 0 ? (totalScore / totalCount / 4 * 100).toFixed(1) : 0;

    const typePerformance = {};
    Object.keys(ACTIVITY_TYPES).forEach(t => {
      const acts = mockActivities.filter(a => a.type === t);
      const partCount = acts.reduce((s, a) => s + a.participants.length, 0);
      const capCount = acts.reduce((s, a) => s + a.maxCapacity, 0);
      typePerformance[t] = capCount > 0 ? Math.round((partCount / capCount) * 100) : 0;
    });

    return {
      optimization: { engagement: avgEngagement, attendance: attendanceRate, satisfaction: 4.6, efficiency: 92.1 },
      predictions: {
        lowAttendance: mockActivities.filter(a => a.participants.length / a.maxCapacity < 0.3).length,
        highDemand: mockActivities.filter(a => a.participants.length / a.maxCapacity > 0.8).length,
        optimalTiming: 8,
        engagementOpportunities: mockResidents.filter(r => !mockActivities.some(a => a.participants.includes(r.id))).length + 4,
      },
      typePerformance,
      recommendations: [
        { id: 'AI001', priority: 'High', title: 'Reschedule Brain Games & Puzzles to morning slot', description: 'Engagement analysis shows 40% higher cognitive activity attendance before 11:00 AM.', impact: 'Increase attendance by 25%', confidence: 91 },
        { id: 'AI002', priority: 'Medium', title: 'Add second Music Therapy session on Monday', description: 'Piano Singalong consistently at capacity \u2014 unmet demand detected from resident interest profiles.', impact: 'Serve 5+ additional residents', confidence: 84 },
        { id: 'AI003', priority: 'Medium', title: 'Pair exercise-reluctant residents with social activities', description: 'Residents R004 and R007 have low exercise participation but high social engagement \u2014 add gentle movement to social events.', impact: 'Improve physical activity compliance', confidence: 78 },
        { id: 'AI004', priority: 'Low', title: 'Optimise activity room allocation', description: 'Thursday shows 3 overlapping sessions \u2014 Library and Arts & Crafts Room could be swapped for better flow.', impact: 'Reduce scheduling conflicts by 20%', confidence: 76 },
      ],
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-6 rounded-2xl border border-purple-200">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-bold text-purple-800 flex items-center gap-2"><Brain size={20} className="text-purple-600" /> AI-Powered Activity Optimisation</h3>
            <p className="text-sm text-purple-600">Intelligent planning, engagement analysis & predictive insights</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-green-600 font-medium">AI Active</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Engagement', value: `${aiData.optimization.engagement}%`, target: 'Target 85%', color: 'green', icon: Heart, pct: aiData.optimization.engagement },
            { label: 'Attendance', value: `${aiData.optimization.attendance}%`, target: 'Fill rate', color: 'blue', icon: Users, pct: aiData.optimization.attendance },
            { label: 'Satisfaction', value: `${aiData.optimization.satisfaction}/5`, target: 'Resident feedback', color: 'purple', icon: Star, pct: aiData.optimization.satisfaction / 5 * 100 },
            { label: 'Efficiency', value: `${aiData.optimization.efficiency}%`, target: 'AI-driven', color: 'emerald', icon: Zap, pct: aiData.optimization.efficiency },
          ].map(m => (
            <Card key={m.label} className="p-4 border border-purple-100">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 bg-${m.color}-500 rounded-lg`}>
                  <m.icon size={18} className="text-white" />
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold text-${m.color}-700`}>{m.value}</p>
                  <p className="text-xs text-gray-500">{m.label}</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className={`bg-${m.color}-500 h-1.5 rounded-full`} style={{ width: `${Math.min(100, m.pct)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{m.target}</p>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2"><Brain size={16} className="text-purple-600" /> AI Recommendations</h4>
          <Badge className="bg-purple-100 text-purple-800">{aiData.recommendations.length} Active</Badge>
        </div>
        <div className="space-y-3">
          {aiData.recommendations.map(rec => (
            <div key={rec.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-semibold text-sm text-gray-800">{rec.title}</span>
                    <Badge className={rec.priority === 'High' ? 'bg-red-100 text-red-800' : rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                      {rec.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{rec.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Target size={11} /> {rec.impact}</span>
                    <span className="flex items-center gap-1"><Brain size={11} /> {rec.confidence}% confidence</span>
                  </div>
                </div>
                <div className="flex gap-1.5 ml-3">
                  <button className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"><CheckCircle size={16} /></button>
                  <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"><X size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h4 className="font-semibold text-gray-800 mb-4">Category Fill Rates</h4>
          <div className="space-y-3">
            {Object.entries(aiData.typePerformance).sort(([,a],[,b]) => b - a).map(([type, pct]) => {
              const cfg = ACTIVITY_TYPES[type] || {};
              return (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-20 text-gray-700">{type}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                    <div className={`h-2.5 rounded-full ${cfg.color || 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-600 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5">
          <h4 className="font-semibold text-gray-800 mb-4">Predictive Insights</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{aiData.predictions.lowAttendance}</p>
              <p className="text-xs text-gray-600">Low Attendance Risk</p>
              <p className="text-xs text-gray-400">Next 7 days</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{aiData.predictions.highDemand}</p>
              <p className="text-xs text-gray-600">High Demand Activities</p>
              <p className="text-xs text-gray-400">Consider expanding</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{aiData.predictions.optimalTiming}</p>
              <p className="text-xs text-gray-600">Optimal Time Slots</p>
              <p className="text-xs text-gray-400">Sessions identified</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{aiData.predictions.engagementOpportunities}</p>
              <p className="text-xs text-gray-600">Engagement Gaps</p>
              <p className="text-xs text-gray-400">Residents to reach</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'calendar', label: 'Activity Calendar', icon: Calendar, count: mockActivities.length },
  { key: 'plans', label: 'Resident Plans', icon: Target, count: mockResidents.length },
  { key: 'participation', label: 'Participation', icon: ClipboardList },
  { key: 'evaluation', label: 'Evaluation', icon: BarChart3 },
  { key: 'alerts', label: 'Alerts', icon: Bell },
  { key: 'volunteers', label: 'Volunteers', icon: UserPlus, count: mockVolunteers.length },
  { key: 'council', label: 'Council', icon: MessageSquare, count: mockCouncilMeetings.length },
  { key: 'ai', label: 'AI Insights', icon: Brain },
];

function Wellbeing() {
  const { state } = useAppContext();
  const [activeTab, setActiveTab] = useState('calendar');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-xl"><Heart size={24} /></div>
            <div>
              <h1 className="text-2xl font-bold">Wellbeing & Lifestyle</h1>
              <p className="text-purple-100 text-sm">Loading wellbeing data...</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <ListSkeleton count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-white/20 rounded-xl">
            <Heart size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Wellbeing & Lifestyle</h1>
            <p className="text-purple-100 text-sm">Activity management, participation tracking & resident engagement</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{mockActivities.length}</p>
            <p className="text-xs text-purple-100">Weekly Activities</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{mockResidents.length}</p>
            <p className="text-xs text-purple-100">Active Residents</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{mockVolunteers.filter(v => v.status === 'Active').length}</p>
            <p className="text-xs text-purple-100">Volunteers</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{mockCouncilMeetings.length}</p>
            <p className="text-xs text-purple-100">Council Meetings</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 bg-white p-2 rounded-xl shadow-sm border border-gray-100">
        {TABS.map(tab => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            icon={tab.icon}
            label={tab.label}
            count={tab.count}
          />
        ))}
      </div>

      {activeTab === 'calendar' && <ActivityCalendarTab />}
      {activeTab === 'plans' && <ResidentPlansTab />}
      {activeTab === 'participation' && <ParticipationTab />}
      {activeTab === 'evaluation' && <EvaluationTab />}
      {activeTab === 'alerts' && <AlertsTab />}
      {activeTab === 'volunteers' && <VolunteersTab />}
      {activeTab === 'council' && <CouncilTab />}
      {activeTab === 'ai' && <AIInsightsTab />}
    </div>
  );
}

export default Wellbeing;
